import React, { useEffect, useMemo, useState } from "react";
import classNames from "classnames";
import Slider from "react-slick";
import arrowLeft from "./assets/arrow_left.png";
import arrowRight from "./assets/arrow_right.png";
import leftWinnerLine from "./assets/left-winner-line.png";
import rightWinnerLine from "./assets/right-winner-line.png";
import leftWinnerLineOne from "./assets/left-winner-line_one.png";
import rightWinnerLineOne from "./assets/right-winner-line_one.png";
import championImage from "./assets/champion.svg";

import styles from "./InstantTournaments.module.scss";
import TournamentPair from "./TournamentPair";
import {
  TournamentPlayerPerTeam,
  TournamentMatch,
  TournamentRound,
  TournamentRoundStage,
  TournamentRoundStatus,
  TournamentMatchTeamResult,
} from "../../../graphql/generated";
import { useTranslation } from "react-i18next";
import { useCurrentRound } from "./hooks/useCurrentRound";
import { AbsoluteCenter } from "../../AbsoluteCenter";
import Lottie from "lottie-react";
import animation from "../../../assets/confetti-explotion.json";
import { useUser } from "../../UserProvider";
import { useLocation } from "react-router";

const Arrow = (props: any) => {
  const { t } = useTranslation();
  const { className, onClick, prev } = props;

  return (
    <div
      className={classNames(props.customStyleClass, {
        ...className,
      })}
      onClick={onClick}
    >
      {prev && <img src={arrowLeft} alt="" />}
      <span>
        {prev ? t("instant_tournament.prev") : t("instant_tournament.next")}
      </span>
      {!prev && <img src={arrowRight} alt="" />}
    </div>
  );
};

const SlickSlider = React.memo(
  ({
    shouldSlide,
    hideNextArrow,
    hidePrevArrow,
    numberOfSlides,
    setCurrentRoundPage,
    children,
  }: {
    shouldSlide: boolean;
    hideNextArrow: boolean;
    hidePrevArrow: boolean;
    children?: React.ReactNode;
    numberOfSlides: number;
    setCurrentRoundPage: (page: number) => void;
  }) => {
    const { t } = useTranslation();

    const [currentPage, setCurrentPage] = React.useState(0);
    if (shouldSlide) {
      let slider: Slider | null = null;
      const onNext = () => {
        slider?.slickNext();
      };

      const onPrev = () => {
        slider?.slickPrev();
      };
      const sliderSettings = {
        dots: false,
        infinite: false,
        speed: 500,
        slidesToShow: 1,
        slidesToScroll: 1,
        arrows: false,
        afterChange: (currentSlide: number) => {
          setCurrentPage(currentSlide);
          setCurrentRoundPage(currentSlide);
        },
      };
      return (
        <div className="h-full relative flex flex-col gap-3">
          <Slider
            ref={(c) => (slider = c)}
            {...sliderSettings}
            className={styles.tournamentSlider}
          >
            {children}
          </Slider>
          {numberOfSlides > 1 && (
            <div className="flex justify-between items-center">
              <p className="text-sm" style={{ color: "#727587" }}>
                {t("instant_tournament.slide")}:{" "}
                <span style={{ color: "#F5F5F5" }}>{`${
                  currentPage + 1
                }/${numberOfSlides}`}</span>
              </p>
              <div className="flex gap-5">
                <Arrow
                  prev
                  customStyleClass={styles.roundSliderArrow}
                  onClick={onPrev}
                  zIndex={hidePrevArrow ? 0 : 1}
                />
                <Arrow
                  customStyleClass={styles.roundSliderArrow}
                  onClick={onNext}
                  zIndex={hideNextArrow ? 0 : 1}
                />
              </div>
            </div>
          )}
        </div>
      );
    }

    return <div>{children}</div>;
  }
);

const RoundLeftMatch = ({
  pair,
  isWinner,
  isOneVSOne,
  isMatchCancelled,
}: {
  pair: TournamentMatch;
  isWinner: number | null;
  isOneVSOne: boolean;
  isMatchCancelled: boolean;
}) => {
  return (
    <div className="h-full  flex flex-col justify-center gap-12">
      <div className="flex flex-col gap-2 relative">
        <TournamentPair
          tableId={pair.tableId!}
          toBeDecided={!pair.teams[0]?.players?.[0]}
          team={pair.teams[0]}
          isWinner={typeof isWinner === "number" ? isWinner === 0 : null}
          isOneVSOne={isOneVSOne}
          isMatchCancelled={isMatchCancelled}
        />
        <div
          className="absolute right-0 bottom-0 flex items-center justify-center"
          style={{
            transform: "translate(93%, 60%)",
            width: "3.25rem",
            height: "auto",
          }}
        >
          <img
            src={isOneVSOne ? leftWinnerLineOne : leftWinnerLine}
            alt="img"
          />
        </div>
      </div>
      <div className="flex flex-col gap-2">
        <TournamentPair
          tableId={pair.tableId!}
          toBeDecided={!pair.teams[1]?.players?.[0]}
          team={pair.teams[1]}
          isWinner={typeof isWinner === "number" ? isWinner === 1 : null}
          isOneVSOne={isOneVSOne}
          isMatchCancelled={isMatchCancelled}
        />
      </div>
    </div>
  );
};

const RoundRightMatch = ({
  pair,
  isWinner,
  isOneVSOne,
  isMatchCancelled,
}: {
  pair: TournamentMatch;
  isWinner: number | null;
  isOneVSOne: boolean;
  isMatchCancelled: boolean;
}) => {
  return (
    <div className="h-full  flex flex-col justify-center gap-12">
      <div className="flex flex-col gap-2 relative">
        <TournamentPair
          tableId={pair.tableId!}
          toBeDecided={!pair.teams[0]?.players?.[0]}
          team={pair.teams[0]}
          isWinner={typeof isWinner === "number" ? isWinner === 0 : null}
          isOneVSOne={isOneVSOne}
          isMatchCancelled={isMatchCancelled}
        />
        <div
          className="absolute left-0 bottom-0 flex items-center justify-center"
          style={{
            transform: "translate(-93%, 60%)",
            width: "3.25rem",
            height: "auto",
          }}
        >
          <img
            src={isOneVSOne ? rightWinnerLineOne : rightWinnerLine}
            alt="img"
          />
        </div>
      </div>
      <div className="flex flex-col gap-2">
        <TournamentPair
          tableId={pair.tableId!}
          toBeDecided={!pair.teams[1]?.players?.[0]}
          team={pair.teams[1]}
          isWinner={typeof isWinner === "number" ? isWinner === 1 : null}
          isOneVSOne={isOneVSOne}
          isMatchCancelled={isMatchCancelled}
        />
      </div>
    </div>
  );
};

export const TournamentRoundsSlider = ({
  hideNextArrow,
  hidePrevArrow,
  round,
  isOneVSOne,
}: {
  hideNextArrow: boolean;
  hidePrevArrow: boolean;
  round: TournamentRound;
  isOneVSOne: TournamentPlayerPerTeam;
}) => {
  const { t } = useTranslation();
  const {
    user: { id },
  } = useUser();
  const { pathname } = useLocation();
  const [currentRoundPage, setCurrentRoundPage] = React.useState(0);
  const getCurrentRound = useCurrentRound();
  const [showWinnerAnimation, setShowWinnerAnimation] = useState(false);

  const isTypeOneVSOne = isOneVSOne === TournamentPlayerPerTeam.OneVsOne;

  const matchesSlides = useMemo(() => {
    const chunked = [];
    for (let i = 0; i < round.matches.length; i += 2) {
      chunked.push(round.matches.slice(i, i + 2));
    }

    return chunked;
  }, [round.matches]);

  const isFinalRound = useMemo(() => {
    return round.stage === TournamentRoundStage.Final;
  }, [round.stage]);

  useEffect(() => {
    if (
      (pathname.includes("instant-tournaments") ||
        pathname.includes("scheduled-tournaments")) &&
      round.stage === TournamentRoundStage.Final
    ) {
      const match = round?.matches[0];
      const winnerTeam = match?.teams?.find(
        (team) => team.ordinalNumber === match?.winnerOrdinalNumber
      );
      const winnerPlayerIds =
        winnerTeam?.players.map((player) => player?.id) ?? [];

      setShowWinnerAnimation(winnerPlayerIds?.includes(id));
    }
  }, [round, id, pathname]);

  return (
    <div className="h-full relative">
      <SlickSlider
        shouldSlide={true}
        hideNextArrow={hideNextArrow}
        hidePrevArrow={hidePrevArrow}
        numberOfSlides={matchesSlides.length || 1}
        setCurrentRoundPage={setCurrentRoundPage}
      >
        {matchesSlides?.map((slideData, index) => {
          return (
            <div
              key={index}
              className={classNames(
                "flex flex-col px-10 py-5 gap-4",
                styles.tournamentSlide,
                { [styles.tournamentSlideFinal]: isFinalRound }
              )}
            >
              <div className="flex justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-white">
                    {getCurrentRound(round.stage)}
                  </span>
                  {!isFinalRound && (
                    <span style={{ color: "rgba(217, 217, 217, 1)" }}>
                      &middot;
                    </span>
                  )}
                  {!isFinalRound && (
                    <span
                      className="text-white text-sm"
                      style={{ color: "rgba(114, 117, 135, 1)" }}
                    >
                      {t("instant_tournament.matches")}{" "}
                      {currentRoundPage * 2 + 1} - {currentRoundPage * 2 + 2}
                    </span>
                  )}
                </div>
              </div>
              {isFinalRound ? (
                <div
                  className="grid grid-cols-3 gap-12 p-3 mt-3 items-center"
                  style={{ height: "calc(100% - 30px)" }}
                >
                  {round?.status === TournamentRoundStatus.Completed && (
                    <div
                      style={{
                        position: "absolute",
                        left: "50%",
                        top: "35%",
                        transform: "translate(-50%, -50%)",
                        width: "28%",
                      }}
                    >
                      <TournamentPair
                        tableId={slideData[0].tableId!}
                        toBeDecided={false}
                        team={
                          slideData[0].teams[slideData[0]?.winnerOrdinalNumber!]
                        }
                        isWinner={true}
                        isOneVSOne={isTypeOneVSOne}
                        isMatchCancelled={
                          slideData[0]?.winnerOrdinalNumber === -1
                        }
                      />
                    </div>
                  )}
                  <TournamentPair
                    tableId={slideData[0].tableId!}
                    toBeDecided={!slideData[0].teams[0].players[0]}
                    team={slideData[0].teams[0]}
                    isWinner={
                      typeof slideData[0]?.winnerOrdinalNumber === "number"
                        ? slideData[0]?.winnerOrdinalNumber === 0
                        : null
                    }
                    isOneVSOne={isTypeOneVSOne}
                    isMatchCancelled={
                      slideData[0]?.winnerOrdinalNumber === -1 ||
                      slideData[0].teams[0].result ===
                        TournamentMatchTeamResult.AutoAdvance
                    }
                  />
                  <div className="h-full  flex flex-col justify-center gap-24" />
                  <TournamentPair
                    tableId={slideData[0].tableId!}
                    toBeDecided={!slideData[0].teams[1].players[0]}
                    team={slideData[0].teams[1]}
                    isWinner={
                      typeof slideData[0]?.winnerOrdinalNumber === "number"
                        ? slideData[0]?.winnerOrdinalNumber === 1
                        : null
                    }
                    isOneVSOne={isTypeOneVSOne}
                    isMatchCancelled={
                      slideData[0]?.winnerOrdinalNumber === -1 ||
                      slideData[0].teams[1].result ===
                        TournamentMatchTeamResult.AutoAdvance
                    }
                  />
                </div>
              ) : (
                <div
                  className="grid grid-cols-3 gap-12 p-3 mt-3"
                  style={{ height: "calc(100% - 30px)" }}
                >
                  <RoundLeftMatch
                    pair={slideData[0] as TournamentMatch}
                    isWinner={slideData[0].winnerOrdinalNumber}
                    isOneVSOne={isTypeOneVSOne}
                    isMatchCancelled={slideData[0].winnerOrdinalNumber === -1}
                  />
                  <div className="h-full  flex flex-col justify-center gap-24">
                    {typeof slideData[0]?.winnerOrdinalNumber === "number" ? (
                      <TournamentPair
                        tableId={slideData[0].tableId!}
                        team={
                          slideData[0].teams[slideData[0].winnerOrdinalNumber!]
                        }
                        isWinner={slideData[0]?.winnerOrdinalNumber >= 0}
                        isOneVSOne={isTypeOneVSOne}
                        isMatchCancelled={
                          slideData[0].winnerOrdinalNumber === -1
                        }
                      />
                    ) : (
                      <TournamentPair
                        toBeDecided
                        isOneVSOne={isTypeOneVSOne}
                        isMatchCancelled={false}
                      />
                    )}
                    {typeof slideData[1]?.winnerOrdinalNumber === "number" ? (
                      <TournamentPair
                        tableId={slideData[1].tableId!}
                        team={
                          slideData[1].teams[slideData[1].winnerOrdinalNumber!]
                        }
                        isWinner={slideData[1]?.winnerOrdinalNumber >= 0}
                        isOneVSOne={isTypeOneVSOne}
                        isMatchCancelled={
                          slideData[1].winnerOrdinalNumber === -1
                        }
                      />
                    ) : (
                      <TournamentPair
                        toBeDecided
                        isOneVSOne={isTypeOneVSOne}
                        isMatchCancelled={false}
                      />
                    )}
                  </div>
                  <RoundRightMatch
                    pair={slideData[1] as TournamentMatch}
                    isWinner={slideData[1].winnerOrdinalNumber}
                    isOneVSOne={isTypeOneVSOne}
                    isMatchCancelled={slideData[1].winnerOrdinalNumber === -1}
                  />
                </div>
              )}
            </div>
          );
        })}
      </SlickSlider>
      {showWinnerAnimation && (
        <div className="absolute bg-black-alpha-30 top-0 left-0 right-0 bottom-0 z-50">
          <AbsoluteCenter>
            <div
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                width: "100%",
                height: "100%",
                transform: "translate(-50%, -50%)",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  top: "0%",
                  left: "50%",
                  width: "50%",
                  height: "50%",
                  transform: "translate(-50%, -50%)",
                }}
              >
                <Lottie animationData={animation} loop={false} autoplay />
              </div>

              <div className={styles.winnerModal}>
                <img src={championImage} alt="champion" />
                <div>
                  <span>{t("instant_tournament.you_won_tournament")}</span>
                  <span>{t("instant_tournament.congratulations")}</span>
                </div>
                <button
                  className="btn btn-green-600 btn-sm w-full rounded"
                  onClick={() => setShowWinnerAnimation(false)}
                >
                  {t("close")}
                </button>
              </div>
            </div>
          </AbsoluteCenter>
        </div>
      )}
    </div>
  );
};
